import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {
  ACTION_CATEGORY,
  ActionMethod,
  ANALYTICS_MODULE,
  BaseAnalyticsMetadata,
} from 'constants/AnalyticsConstants';
import {SampleStatusWarningType} from 'constants/SampleStatusConstants';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import EntityStore from 'stores/EntityStore';

export type SampleStatusActionType =
  | 'reserve'
  | 'check_out'
  | 'check_in'
  | 'move_location'
  | 'transfer'
  | 'transfer_plate'
  | 'rename'
  | 'update_barcode'
  | 'update_quantity'
  | 'update_concentration'
  | 'update_expiration_date'
  | 'update_fields'
  | 'update_freeze_thaw_count'
  | 'archive'
  | 'unarchive'
  | 'remove_contents';

export interface CheckSampleStatusParams {
  actionAttempted: SampleStatusActionType;
  eventGroupId: string;
}

export interface BaseSampleStatusWarningTrackingParams extends BaseAnalyticsMetadata {
  actionAttempted: SampleStatusActionType;
}

export interface SampleStatusWarningTrackingParams extends BaseSampleStatusWarningTrackingParams {
  sampleIds: Array<string>;
}

interface BaseStatusWarningsMetadata {
  hasRestrictionWarnings: boolean;
  hasCicoWarnings: boolean;
  canDeselectSamples: boolean;
}

interface WarningShownSampleStatusTrackingParams
  extends SampleStatusWarningTrackingParams,
    BaseStatusWarningsMetadata {
  restrictionWarningsAreBlocking: boolean;
  cicoWarningsAreBlocking: boolean;
  warningType: SampleStatusWarningType;
}

interface WarningHandledSampleStatusTrackingParams extends SampleStatusWarningTrackingParams {
  actionMethod: ActionMethod.BANNER_ACTION_CLICK | ActionMethod.MANUAL_TABLE_SELECT;
  warningType: SampleStatusWarningType;
}

function trackWarningShown(params: WarningShownSampleStatusTrackingParams): void {
  const {sampleIds, ...restParams} = params;
  analytics.trackEvent(
    {
      module: ANALYTICS_MODULE.SAMPLES,
      action: 'warning_shown_sample_control_status',
      actionCategory: ACTION_CATEGORY.READ,
    },
    {...restParams, userId: EntityStore.currentUser()?.id, itemIds: sampleIds}
  );
}

function trackWarningHandled(action: string, params: SampleStatusWarningTrackingParams): void {
  const {sampleIds, ...restParams} = params;
  analytics.trackEvent(
    {
      module: ANALYTICS_MODULE.SAMPLES,
      action,
      actionCategory: ACTION_CATEGORY.READ,
    },
    {...restParams, userId: EntityStore.currentUser()?.id, itemIds: sampleIds}
  );
}

function trackWarningOverridden(params: WarningHandledSampleStatusTrackingParams): void {
  trackWarningHandled('warning_overridden_sample_control_status', params);
}

function trackDeselectedItems(params: WarningHandledSampleStatusTrackingParams): void {
  trackWarningHandled('deselected_all_restricted_items', params);
}

function trackStatusWarningShown(
  trackingParamsWithSampleIds: SampleStatusWarningTrackingParams,
  warningType: SampleStatusWarningType,
  baseMetadata: BaseStatusWarningsMetadata
): void {
  trackWarningShown({
    ...trackingParamsWithSampleIds,
    ...baseMetadata,
    restrictionWarningsAreBlocking: hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3),
    cicoWarningsAreBlocking: false,
    warningType,
  });
}

function trackCICOWarningIfShown(
  trackingParamsWithSampleIds: SampleStatusWarningTrackingParams,
  baseMetadata: BaseStatusWarningsMetadata
): void {
  if (baseMetadata.hasCicoWarnings) {
    trackStatusWarningShown(trackingParamsWithSampleIds, SampleStatusWarningType.CICO_WARNING, baseMetadata);
  }
}

function trackRestrictionWarningIfShown(
  trackingParamsWithSampleIds: SampleStatusWarningTrackingParams,
  baseMetadata: BaseStatusWarningsMetadata
): void {
  if (baseMetadata.hasRestrictionWarnings) {
    trackStatusWarningShown(
      trackingParamsWithSampleIds,
      SampleStatusWarningType.SAMPLE_CONTROLS_WARNING,
      baseMetadata
    );
  }
}

export function trackStatusWarningDismissed(
  warningType: SampleStatusWarningType,
  dismissedViaBanner: boolean,
  trackingParamsWithSampleIds: SampleStatusWarningTrackingParams
): void {
  const fullTrackingParams: WarningHandledSampleStatusTrackingParams = {
    ...trackingParamsWithSampleIds,
    warningType,
    actionMethod: dismissedViaBanner ? ActionMethod.BANNER_ACTION_CLICK : ActionMethod.MANUAL_TABLE_SELECT,
  };
  if (
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V3) &&
    warningType === SampleStatusWarningType.SAMPLE_CONTROLS_WARNING
  ) {
    trackDeselectedItems(fullTrackingParams);
  } else {
    trackWarningOverridden(fullTrackingParams);
  }
}

export function trackStatusWarningChanges({
  trackingParamsWithSampleIds,
  shouldCheckCICOWarnings,
  shouldCheckRestrictionWarnings,
  baseMetadata,
  trackIfRestrictionDismissed,
  dismissMetadata,
}: {
  trackingParamsWithSampleIds: SampleStatusWarningTrackingParams;
  shouldCheckCICOWarnings: boolean;
  shouldCheckRestrictionWarnings: boolean;
  baseMetadata: BaseStatusWarningsMetadata;
  trackIfRestrictionDismissed: boolean;
  dismissMetadata?: {dismissedRestrictionViaBanner: boolean; selectedSampleIds: Array<string>};
}): void {
  if (shouldCheckCICOWarnings) {
    trackCICOWarningIfShown(trackingParamsWithSampleIds, baseMetadata);
  }
  if (shouldCheckRestrictionWarnings) {
    trackRestrictionWarningIfShown(trackingParamsWithSampleIds, baseMetadata);

    if (trackIfRestrictionDismissed && !baseMetadata.hasRestrictionWarnings) {
      assert(dismissMetadata != null, 'Expected dismiss metadata to be provided');
      trackStatusWarningDismissed(
        SampleStatusWarningType.SAMPLE_CONTROLS_WARNING,
        dismissMetadata.dismissedRestrictionViaBanner,
        {...trackingParamsWithSampleIds, sampleIds: dismissMetadata.selectedSampleIds}
      );
    }
  }
}
